import { Link } from "react-router-dom";
import { type Language, getPageTranslations } from "../lib/translations";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Calendar, Tag, BookOpen } from "lucide-react";

export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  description: string;
  categories: string[];
  tags: string[];
  content: string;
  readingTime?: number;
}

interface BlogPostCardProps {
  post: BlogPost;
  lang: Language;
}

export function BlogPostCard({ post, lang }: BlogPostCardProps) {
  const t = getPageTranslations(lang).blog;
  return (
    <Link to={`/${lang}/blog/${post.slug}`} className="block group">
      <Card className="border border-border/40 bg-card/45 backdrop-blur-md transition-all duration-300 group-hover:border-brand-primary/30 group-hover:shadow-xl group-hover:shadow-brand-primary/5 group-hover:-translate-y-1 rounded-2xl flex flex-col justify-between h-full text-left">
        <CardHeader className="p-5 md:p-6 space-y-2">
          <div className="flex items-center gap-4 text-[10px] font-mono text-muted-foreground">
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{post.date}</span>
            {post.readingTime !== undefined && (
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                {post.readingTime} {t.minRead || "min read"}
              </span>
            )}
          </div>
          <CardTitle className="text-lg font-extrabold text-foreground group-hover:text-brand-primary transition-colors">
            {post.title}
          </CardTitle>
          <p className="text-sm text-foreground/70 leading-relaxed font-sans break-words mt-1">
            {post.description}
          </p>
        </CardHeader>
        <CardContent className="px-5 pb-5 md:px-6 md:pb-6 flex flex-wrap gap-1.5 pt-0">
          {post.categories && post.categories.map((cat) => (
            <span key={cat} className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-brand-primary bg-brand-primary/5 border border-brand-primary/10 px-2 py-0.5 rounded-full">
              {cat}
            </span>
          ))}
          {post.tags.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 text-[9px] uppercase font-bold text-brand-secondary bg-brand-secondary/5 border border-brand-secondary/10 px-2 py-0.5 rounded-full">
              <Tag className="h-2.5 w-2.5" />
              {tag}
            </span>
          ))}
        </CardContent>
      </Card>
    </Link>
  );
}
